import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Autocomplete, Icon, IconButton } from "@mui/material";

import Box from "../Box";
import TextField from "../TextField";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import Pagination from "../DataTable/Pagination";
import useStrings from "../../hooks/useStrings";



interface IProps {
    total: number;
    page: number;
    pageSize: number;
    isList?: boolean;
    rowsPerPageOptions: number[];
    onChangePage?: (value: any) => void;
    onChangePageSize?: (value: any) => void;
}

const CustomPagination: React.FC<IProps> = (props: IProps) => {
    const strings = useStrings();

    const page = useMemo(() => (props.page - 1), [props.page]);
    const totalCount = useMemo(() => (props.total), [props.total]);
    const pageSize = useMemo(() => (props.pageSize), [props.pageSize]);

    let to = 0;
    let from = 0;

    if ((page + 1) <= Math.ceil(totalCount / pageSize) && page >= 0) {
        to = Math.min((page + 1) * pageSize, totalCount);
        from = Math.min(page * pageSize + 1, to);
    }

    const range = useCallback((currentPage: number) => {
        let range = [];

        const end = Math.ceil(totalCount / pageSize);

        if (end <= 7) {
            for (let i = 0; i < end; i++) {
                range.push(i + 1);
            };

            return range;
        } else {
            if (currentPage <= 3) {
                range = [1, 2, 3, "...", end];
                return range;
            } else {
                let middle = (currentPage < (end - 2))
                    ? [currentPage - 1, currentPage, currentPage + 1, "..."]
                    : [end - 2, end - 1];

                range = [
                    ...[1, "..."],
                    ...middle,
                    ...[end]
                ];

                return range;
            }
        }
    }, [pageSize, totalCount])

    const [pagePanel, setPagePanel] = useState(range(page + 1));

    useEffect(() => {
        setPagePanel(range(page + 1));
    }, [page, range]);

    // const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    //     setPagePanel(range(0));
    //     props.onChangePage(0 + 1);
    // };

    // const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    //     setPagePanel(range(Math.max(0, Math.ceil(totalCount / pageSize) - 1)));
    //     props.onChangePage(Math.max(0, Math.ceil(totalCount / pageSize) - 1));
    // };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (page !== 0) {
            setPagePanel(range(page - 1));
            Helpers.isFunction(props.onChangePage) && props.onChangePage((page - 1) + 1);
        }
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (page < (Math.ceil(totalCount / pageSize) - 1)) {
            setPagePanel(range(page + 1));
            Helpers.isFunction(props.onChangePage) && props.onChangePage((page + 1) + 1);
        }
    };

    const handlePageClick = (event: React.MouseEvent<HTMLButtonElement>, pageIndex: number) => {
        setPagePanel(range(pageIndex));
        Helpers.isFunction(props.onChangePage) && props.onChangePage(pageIndex + 1);
    }

    return (
        <>
            <Box
                p={2}
                display="flex"
                justifyContent="end"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
            >
                {/* Page Size */}
                <Box display="flex" alignItems="center" mr={3}>
                    <Typography variant="button" color="secondary" fontWeight="regular">
                        {strings.Common.DISPLAY}:
                    </Typography>
                    <Autocomplete
                        size="small"
                        disableClearable
                        options={props.rowsPerPageOptions || []}
                        sx={{ width: "5rem", marginLeft: "10px" }}
                        getOptionLabel={(option: any) => `${option}`}
                        key={pageSize || props.rowsPerPageOptions[0]}
                        value={pageSize || props.rowsPerPageOptions[0]}
                        renderInput={(params) => <TextField {...params} variant="outlined" />}
                        disabled={!Helpers.isFunction(props.onChangePageSize)}
                        onChange={(event, value) => {
                            Helpers.isFunction(props.onChangePageSize) &&
                                props.onChangePageSize(value);
                        }}
                    />
                </Box>
                <Box mb={{ xs: 3, sm: 0 }} mr={3}>
                    <Typography variant="button" color="secondary" fontWeight="regular">
                        {strings.format(strings.Common.LABEL_DISPLAY_ROWS, from || 0, to || 0, totalCount || 0)}
                    </Typography>
                </Box>
                {/* Page Number */}
                {
                    (pageSize < totalCount) &&
                    <Pagination
                        color={"info"}
                        variant={"gradient"}
                    >
                        {
                            (props.isList)
                                ? (
                                    <>
                                        {
                                            (page !== 0) &&
                                            <Pagination item key="previous_page" onClick={handleBackButtonClick}>
                                                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                                            </Pagination>
                                        }
                                        {
                                            pagePanel.map((item, index) => {
                                                if (item === "...") {
                                                    return (
                                                        <IconButton disabled key={`disabled_${index}`}>
                                                            {item}
                                                        </IconButton>
                                                    );
                                                }
                                                return (
                                                    <Pagination
                                                        item
                                                        key={index}
                                                        active={page === (Number(item) - 1)}
                                                        onClick={(e: any) => handlePageClick(e, +item - 1)}
                                                    >
                                                        {item}
                                                    </Pagination>
                                                );
                                            })
                                        }
                                        {
                                            (page < (Math.ceil(totalCount / pageSize) - 1)) &&
                                            <Pagination item key="next_page" onClick={handleNextButtonClick} >
                                                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                                            </Pagination>
                                        }
                                    </>
                                )
                                : (
                                    <>
                                        <Pagination item key="previous_page"
                                            onClick={handleBackButtonClick}
                                            variant={(page !== 0) ? "gradient" : undefined}
                                        >
                                            <Icon sx={{ fontWeight: "bold", color: (page !== 0) ? "blue" : "black" }}>chevron_left</Icon>
                                        </Pagination>
                                        <Pagination item active key={page}>
                                            {(page + 1)}
                                        </Pagination>
                                        <Pagination item
                                            key="next_page"
                                            onClick={handleNextButtonClick}
                                            variant={(page < (Math.ceil(totalCount / pageSize) - 1)) ? "gradient" : undefined}
                                        >
                                            <Icon sx={{ fontWeight: "bold", color: (page < (Math.ceil(totalCount / pageSize) - 1)) ? "blue" : "black" }}>chevron_right</Icon>
                                        </Pagination>
                                    </>
                                )
                        }
                    </Pagination>
                }
            </Box>
        </>
    );
}

export default CustomPagination;